/**
 * User Management Service API
 *
 * Service layer for User Management Service operations
 * Handles authentication, registration, profile management, and role assignment
 */

import userManagementClient from '../clients/user-management.client';
import {
  LoginRequest,
  LoginResponse,
  RefreshTokenRequest,
  RefreshTokenResponse,
  LogoutRequest,
  LogoutResponse,
  RegisterRequest,
  RegisterResponse,
  FileUploadResponse,
  UserProfile,
  UpdateProfileRequest,
  ChangePasswordRequest,
  ChangePasswordResponse,
  TokenValidationResponse,
  UserPermissionsResponse,
  UserRolesResponse,
  RoleDefinition,
  AssignRoleRequest,
  MessageResponse,
  HealthResponse,
  UserRole,
  UsersByRoleResponse,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
} from '../types/user-management.types';
import { STORAGE_KEYS } from '../config';
import { isApiErrorWithStatus, AccountAlreadyExistsError } from '../errors';

// ===========================
// AUTHENTICATION SERVICES
// ===========================

export const authService = {
  /**
   * Login user and store tokens
   */
  async login(data: LoginRequest): Promise<LoginResponse> {
    const response = await userManagementClient.post('/login', data);
    const loginData = response.data;

    // Store tokens only (user info is decoded from JWT in AuthContext)
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, loginData.accessToken);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, loginData.refreshToken);

    return loginData;
  },

  /**
   * Refresh access token
   */
  async refreshToken(data: RefreshTokenRequest): Promise<RefreshTokenResponse> {
    const response = await userManagementClient.post('/refresh-token', data);
    const refreshData = response.data;

    // Update stored access token
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, refreshData.accessToken);

    return refreshData;
  },

  /**
   * Logout user and clear tokens
   */
  async logout(data?: LogoutRequest): Promise<LogoutResponse> {
    try {
      const response = await userManagementClient.post('/logout', data || {});
      return response.data;
    } finally {
      // Always clear local storage, even if request fails
      this.clearAuthData();
    }
  },

  /**
   * Clear authentication data from localStorage
   */
  clearAuthData(): void {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  },

  /**
   * Get stored access token
   */
  getAccessToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  },

  /**
   * Get stored refresh token
   */
  getRefreshToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  },

  /**
   * Validate current token (service-to-service)
   */
  async validateToken(): Promise<TokenValidationResponse> {
    const response = await userManagementClient.post('/validate-token');
    return response.data;
  },

  /**
   * Request password reset email
   * Always returns success to prevent email enumeration attacks
   */
  async forgotPassword(data: ForgotPasswordRequest): Promise<ForgotPasswordResponse> {
    const response = await userManagementClient.post<ForgotPasswordResponse>(
      '/forgot-password',
      data
    );
    return response.data;
  },

  /**
   * Reset password using token from email
   */
  async resetPassword(data: ResetPasswordRequest): Promise<ResetPasswordResponse> {
    const response = await userManagementClient.post<ResetPasswordResponse>(
      '/reset-password',
      data
    );
    return response.data;
  },
};

// ===========================
// REGISTRATION SERVICES
// ===========================

export const registrationService = {
  /**
   * Upload business license file
   */
  async uploadBusinessLicense(file: File): Promise<FileUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await userManagementClient.post('/files/business-license', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Register a new publisher user
   */
  async register(data: RegisterRequest): Promise<RegisterResponse> {
    try {
      const response = await userManagementClient.post('/register', data);
      return response.data;
    } catch (error: unknown) {
      if (isApiErrorWithStatus(error) && error.status === 409) {
        throw new AccountAlreadyExistsError();
      }
      throw error;
    }
  },

  /**
   * Complete registration flow with file upload
   */
  async completeRegistration(
    registrationData: Omit<RegisterRequest, 'businessLicenseFileId'>,
    businessLicenseFile: File
  ): Promise<RegisterResponse> {
    // Step 1: Upload business license file
    const fileUpload = await this.uploadBusinessLicense(businessLicenseFile);

    // Step 2: Register with file ID
    const completeData: RegisterRequest = {
      ...registrationData,
      businessLicenseFileId: fileUpload.fileId,
    };

    return this.register(completeData);
  },
};

// ===========================
// PROFILE SERVICES
// ===========================

export const profileService = {
  /**
   * Get current user profile
   */
  async getProfile(): Promise<UserProfile> {
    const response = await userManagementClient.get('/profile');
    return response.data;
  },

  /**
   * Update user profile
   */
  async updateProfile(data: UpdateProfileRequest): Promise<UserProfile> {
    const response = await userManagementClient.put('/profile', data);
    return response.data;
  },

  /**
   * Change password
   */
  async changePassword(data: ChangePasswordRequest): Promise<ChangePasswordResponse> {
    const response = await userManagementClient.post('/profile/change-password', data);
    return response.data;
  },

  /**
   * Get user permissions
   */
  async getUserPermissions(userId: string): Promise<UserPermissionsResponse> {
    const response = await userManagementClient.get(`/users/${userId}/permissions`);
    return response.data;
  },

  /**
   * Get current user permissions
   * Note: Requires userId to be passed - get it from AuthContext
   */
  async getCurrentUserPermissions(userId: string): Promise<UserPermissionsResponse> {
    if (!userId) {
      throw new Error('User ID is required');
    }
    return this.getUserPermissions(userId);
  },
};

// ===========================
// ROLE MANAGEMENT SERVICES
// ===========================

export const roleService = {
  /**
   * Get user roles
   */
  async getUserRoles(userId: string): Promise<UserRolesResponse> {
    const response = await userManagementClient.get(`/users/${userId}/roles`);
    return response.data;
  },

  /**
   * Assign role to user (Admin only)
   */
  async assignRole(userId: string, data: AssignRoleRequest): Promise<MessageResponse> {
    const response = await userManagementClient.post(`/users/${userId}/roles`, data);
    return response.data;
  },

  /**
   * Remove role from user (Admin only)
   */
  async removeRole(userId: string, roleCode: string): Promise<MessageResponse> {
    const response = await userManagementClient.delete(`/users/${userId}/roles`, {
      params: { roleCode },
    });
    return response.data;
  },

  /**
   * Get all available roles
   */
  async getAllRoles(): Promise<RoleDefinition[]> {
    const response = await userManagementClient.get('/roles');
    return response.data;
  },

  /**
   * Get all users by role
   * @param roleName - The role to filter users by (e.g., 'PUBLISHER', 'ALC_BACKOFFICE_BOOK_REVIEWER')
   * @returns List of users with the specified role
   */
  async getUsersByRole(roleName: UserRole): Promise<UsersByRoleResponse> {
    const response = await userManagementClient.get('/', {
      headers: {
        'X-Role-Name': roleName,
      },
    });
    return response.data;
  },
};

// ===========================
// HEALTH CHECK SERVICES
// ===========================

export const healthService = {
  /**
   * Get service health status
   */
  async getHealth(): Promise<HealthResponse> {
    const response = await userManagementClient.get('/health');
    return response.data;
  },
};

// ===========================
// COMBINED API OBJECT
// ===========================

/**
 * Complete User Management API service
 * Combines all service modules for easy import
 */
export const userManagementApi = {
  auth: authService,
  registration: registrationService,
  profile: profileService,
  roles: roleService,
  health: healthService,
};

export default userManagementApi;

// ===========================
// AUTHENTICATION UTILITIES
// ===========================

/**
 * Check if current user has specific role
 */
export function hasRole(role: string): boolean {
  const user = authService.getCurrentUser();
  return user?.roles?.includes(role) || false;
}

/**
 * Check if current user has any of the specified roles
 */
export function hasAnyRole(roles: string[]): boolean {
  const user = authService.getCurrentUser();
  if (!user?.roles) return false;

  return roles.some(role => user.roles.includes(role));
}

/**
 * Check if current user has all of the specified roles
 */
export function hasAllRoles(roles: string[]): boolean {
  const user = authService.getCurrentUser();
  if (!user?.roles) return false;

  return roles.every(role => user.roles.includes(role));
}

/**
 * Get user's role type for UI purposes
 */
export function getUserRoleType(): 'publisher' | 'backoffice' | 'admin' | 'unknown' {
  const user = authService.getCurrentUser();
  if (!user?.roles) return 'unknown';

  if (user.roles.includes('ADMIN')) return 'admin';
  if (user.roles.includes('ALC_BACKOFFICE') || user.roles.includes('ARABOOKVERSE_BACKOFFICE')) {
    return 'backoffice';
  }
  if (user.roles.includes('PUBLISHER')) return 'publisher';

  return 'unknown';
}

// ===========================
// VALIDATION UTILITIES
// ===========================

/**
 * Validate email format
 */
export function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Validate password strength
 * Requirements: min 8 chars, 1 uppercase, 1 lowercase, 1 number, 1 special char
 */
export function validatePassword(password: string): {
  valid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  if (password.length < 8) {
    errors.push('Password must be at least 8 characters long');
  }

  if (!/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }

  if (!/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }

  if (!/\d/.test(password)) {
    errors.push('Password must contain at least one number');
  }

  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    errors.push('Password must contain at least one special character');
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Validate phone number (E.164 format)
 */
export function validatePhoneNumber(phone: string): boolean {
  const phoneRegex = /^\+[1-9]\d{1,14}$/;
  return phoneRegex.test(phone);
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Validate file upload for business license
 */
export function validateBusinessLicense(file: File): {
  valid: boolean;
  error?: string;
} {
  const maxSizeMB = 10;
  const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];

  // Check file type
  if (!allowedTypes.includes(file.type)) {
    return {
      valid: false,
      error: 'File must be PDF, PNG, JPG, or JPEG format',
    };
  }

  // Check file size (10MB limit)
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    return {
      valid: false,
      error: `File size must be less than ${maxSizeMB}MB`,
    };
  }

  return { valid: true };
}